import "../../i18n/types";
import { Badge } from "../badge/badge";
import {
  Dialog,
  DialogContent,
  type DialogContentProps,
  DialogDescription,
  type DialogProps,
  DialogTitle,
} from "../dialog/dialog";
import { Icon } from "../icons/icon";
import { cn } from "../../lib/utils";
import { Command as CommandPrimitive } from "cmdk";
import * as React from "react";
import { useTranslation } from "react-i18next";

/**
 * Root command menu. Transparent, fills its container: it carries NO card chrome
 * (border, shadow, radius). The container (a Dialog, the floating palette shell,
 * or a bordered card) supplies the chrome so a Command inside a Dialog is not a
 * card-in-a-card. See command.contract.md.
 */
function Command({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive>) {
  return (
    <CommandPrimitive
      data-slot="command"
      className={cn(
        "flex h-full w-full flex-col overflow-hidden bg-popover text-popover-foreground",
        className,
      )}
      {...props}
    />
  );
}

type CommandInputProps = React.ComponentProps<typeof CommandPrimitive.Input> & {
  /** Class applied to the input row wrapper (border, padding). */
  wrapperClassName?: string;
  /** Accessible label for the trailing clear action. */
  clearLabel?: string;
  /**
   * Show the trailing clear (X). Defaults to auto: visible when the input has a
   * non-empty value. The clear works whether the input is controlled or not —
   * `CommandInput` owns its value internally when no `value` prop is passed.
   */
  showClear?: boolean;
};

/**
 * Optionally-controlled value: uses the callsite's `value` when provided,
 * otherwise owns it internally. Always forwards changes to `onValueChange`.
 */
function useControllableInputValue(
  value: string | undefined,
  onValueChange: ((value: string) => void) | undefined,
) {
  const [internal, setInternal] = React.useState("");
  const isControlled = value !== undefined;
  const current = isControlled ? value : internal;
  const setValue = React.useCallback(
    (next: string) => {
      if (!isControlled) setInternal(next);
      onValueChange?.(next);
    },
    [isControlled, onValueChange],
  );
  return [current, setValue] as const;
}

function CommandInput({
  className,
  wrapperClassName,
  clearLabel,
  showClear,
  value,
  onValueChange,
  disabled,
  ref,
  ...props
}: CommandInputProps) {
  const { t } = useTranslation("ds");
  const inputRef = React.useRef<HTMLInputElement>(null);
  const setInputRef = React.useCallback(
    (node: HTMLInputElement | null) => {
      (inputRef as React.MutableRefObject<HTMLInputElement | null>).current =
        node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref != null && typeof ref === "object") {
        (ref as React.MutableRefObject<HTMLInputElement | null>).current =
          node;
      }
    },
    [ref],
  );
  // Own the value when the callsite doesn't control it, so the clear (X) can
  // reset an otherwise-uncontrolled cmdk input (cmdk exposes no search setter).
  const [currentValue, setValue] = useControllableInputValue(
    value,
    onValueChange,
  );
  const canClear = (showClear ?? Boolean(currentValue)) && !disabled;

  return (
    <div
      data-slot="command-input-wrapper"
      data-disabled={disabled || undefined}
      className={cn(
        "flex items-center gap-2 border-b px-3 data-[disabled=true]:opacity-50",
        wrapperClassName,
      )}
    >
      <Icon src="Search" className="size-4 shrink-0 opacity-50" />
      <CommandPrimitive.Input
        data-slot="command-input"
        ref={setInputRef}
        value={currentValue}
        onValueChange={setValue}
        disabled={disabled}
        className={cn(
          "flex h-11 w-full min-w-0 bg-transparent text-sm outline-hidden placeholder:text-muted-foreground disabled:cursor-not-allowed",
          className,
        )}
        {...props}
      />
      {canClear && (
        <button
          type="button"
          data-slot="command-input-clear"
          aria-label={clearLabel ?? t("common.clear_search")}
          onClick={() => {
            setValue("");
            inputRef.current?.focus();
          }}
          className="inline-flex size-6 shrink-0 items-center justify-center rounded-sm text-muted-foreground outline-hidden transition-colors hover:bg-accent hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring [&_svg:not([class*='size-'])]:size-4"
        >
          <Icon src="X" />
        </button>
      )}
    </div>
  );
}

function CommandList({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      data-slot="command-list"
      className={cn(
        "max-h-[300px] scroll-py-1 overflow-x-hidden overflow-y-auto",
        className,
      )}
      {...props}
    />
  );
}

function CommandEmpty({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return (
    <CommandPrimitive.Empty
      data-slot="command-empty"
      className={cn(
        "py-6 text-center text-sm text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}

function CommandGroup({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={cn(
        "overflow-hidden px-2 py-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}

function CommandSeparator({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      data-slot="command-separator"
      aria-hidden="true"
      role="presentation"
      className={cn("-mx-1 h-px bg-border", className)}
      {...props}
    />
  );
}

/**
 * Selectable row. Renders raw children as-is; compose the CommandItem* slots
 * (media, content, title, breadcrumb, status dot, subtitle, trailing, selected
 * check) for the full Figma item anatomy.
 */
function CommandItem({
  className,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Item>) {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(
        "relative flex cursor-default items-center gap-2 rounded-sm p-2 text-sm outline-hidden select-none data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50 data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  );
}

function CommandShortcut({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="command-shortcut"
      className={cn(
        "ml-auto text-xs tracking-widest text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}

// --- CommandItem* composite ----------------------------------------------
// Command-owned layout slots that duplicate — in spirit, not by import — the
// Combobox ComboboxOption* family. Pure data-slot spans: they own no role,
// selection, disabled, or interaction state.

/** Leading avatar / icon slot. */
function CommandItemMedia({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="command-item-media"
      className={cn(
        "flex size-6 shrink-0 items-center justify-center",
        className,
      )}
      {...props}
    />
  );
}

/** Vertical stack holding the title row and the subtitle. */
function CommandItemContent({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="command-item-content"
      className={cn("flex min-w-0 flex-1 flex-col gap-1", className)}
      {...props}
    />
  );
}

/** Horizontal title line: parent breadcrumb + title + status dot. */
function CommandItemTitleRow({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="command-item-title-row"
      className={cn("flex min-w-0 items-center gap-1.5", className)}
      {...props}
    />
  );
}

/** Primary label text. */
function CommandItemTitle({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="command-item-title"
      className={cn("min-w-0 truncate text-foreground", className)}
      {...props}
    />
  );
}

/**
 * Hierarchical prefix rendered before the title. Inserts a chevron between each
 * crumb; compose with CommandItemCrumb.
 */
function CommandItemParentBreadcrumb({
  className,
  children,
  ...props
}: React.ComponentProps<"span">) {
  const crumbs = React.Children.toArray(children).filter(Boolean);
  const getCrumbKey = (crumb: React.ReactNode) => {
    if (React.isValidElement(crumb) && crumb.key != null) return crumb.key;
    if (typeof crumb === "string" || typeof crumb === "number") return crumb;
    return String(crumb);
  };

  return (
    <span
      data-slot="command-item-parent-breadcrumb"
      className={cn("flex min-w-0 items-center gap-1.5", className)}
      {...props}
    >
      {crumbs.map((crumb, index) => (
        <React.Fragment key={getCrumbKey(crumb)}>
          {index > 0 && (
            <Icon src="ChevronRight" className="size-3 text-muted-foreground" />
          )}
          {crumb}
        </React.Fragment>
      ))}
    </span>
  );
}

/** A single breadcrumb crumb. Mark the final entity with `current`. */
function CommandItemCrumb({
  className,
  current = false,
  ...props
}: React.ComponentProps<"span"> & {
  /** Marks the final/current entity crumb. */
  current?: boolean;
}) {
  return (
    <span
      data-slot="command-item-crumb"
      data-current={current || undefined}
      className={cn(
        "min-w-0 truncate",
        current ? "text-foreground" : "text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}

/** Figma CircleDot status icon on the title line. Color it with `text-*`. */
function CommandItemStatusDot({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="command-item-status-dot"
      className={cn("flex size-4 shrink-0 text-muted-foreground", className)}
      {...props}
    >
      <Icon src="CircleDot" className="size-4" />
    </span>
  );
}

/** Secondary line under the title (additional info). */
function CommandItemSubtitle({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="command-item-subtitle"
      className={cn(
        "truncate text-xs leading-4 text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}

/** Passive end-aligned metadata (secondary info, code, badge). */
function CommandItemTrailing({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="command-item-trailing"
      className={cn(
        "ml-auto max-w-[60%] shrink truncate text-xs leading-4 text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}

/** End-aligned check shown on the selected row (Figma selected state). */
function CommandItemSelectedCheck({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="command-item-selected-check"
      className={cn(
        "ml-auto flex shrink-0 items-center text-foreground",
        className,
      )}
      {...props}
    >
      <Icon src="Check" className="size-4" />
    </span>
  );
}

// --- CommandContextBadge -------------------------------------------------

type CommandContextBadgeProps = React.ComponentProps<typeof Badge>;

/**
 * Static secondary Badge scoped to Command, for context/filter chips above or
 * around the input. A thin wrapper over the DS `Badge` (`secondary` visuals)
 * that stays shrinkable so long labels truncate within a constrained row.
 */
function CommandContextBadge({
  className,
  children,
  ...props
}: CommandContextBadgeProps) {
  return (
    <Badge
      variant="secondary"
      data-slot="command-context-badge"
      className={cn("max-w-full", className)}
      {...props}
    >
      {children}
    </Badge>
  );
}

// --- CommandDialog -------------------------------------------------------

type CommandDialogProps = Pick<
  DialogProps,
  "open" | "onOpenChange" | "defaultOpen" | "modal" | "mobileMode"
> & {
  /** Accessible dialog title (visually hidden). */
  title: string;
  /** Accessible description (visually hidden). Omit to skip. */
  description?: string;
  /** DS Dialog size preset — controls width. Height is fixed. */
  size?: DialogContentProps["size"];
  /** Optional trigger (e.g. a `DialogTrigger` wrapping a Button). */
  trigger?: React.ReactNode;
  /** Extra classes for the dialog content (e.g. a height override). */
  className?: string;
  /** A transparent `Command` with its `CommandInput` and `CommandList`. */
  children: React.ReactNode;
};

/**
 * A command palette in a fixed-height dialog. The dialog owns the card chrome,
 * a stable height (so the box does not grow as results stream in), and makes
 * the `CommandList` fill the space under the input and scroll — so callsites
 * never wire height or chrome by hand.
 */
function CommandDialog({
  title,
  description,
  size = "md",
  trigger,
  className,
  mobileMode = "dialog",
  children,
  ...props
}: CommandDialogProps) {
  return (
    <Dialog mobileMode={mobileMode} {...props}>
      {trigger}
      <DialogContent
        size={size}
        showCloseButton={false}
        className={cn(
          "h-120 max-h-[calc(100svh-2rem)] min-h-0 overflow-hidden p-0",
          // Let the list fill the space under the input and scroll, instead of
          // capping at its popover default height and leaving a gap below.
          "**:data-[slot=command-list]:max-h-none **:data-[slot=command-list]:min-h-0 **:data-[slot=command-list]:flex-1",
          className,
        )}
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogDescription className="sr-only">
          {description ?? title}
        </DialogDescription>
        {children}
      </DialogContent>
    </Dialog>
  );
}

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
  CommandShortcut,
  CommandItemMedia,
  CommandItemContent,
  CommandItemTitleRow,
  CommandItemTitle,
  CommandItemParentBreadcrumb,
  CommandItemCrumb,
  CommandItemStatusDot,
  CommandItemSubtitle,
  CommandItemTrailing,
  CommandItemSelectedCheck,
  CommandContextBadge,
};
